import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  Box,
  Paper,
  Typography,
  Card,
  CardContent,
  Chip,
  Avatar,
  IconButton,
  Button,
  TextField,
  InputAdornment,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  SelectChangeEvent,
  Tooltip,
  Badge,
  Divider,
} from '@mui/material';
import {
  Search,
  FilterList,
  MoreVert,
  Person,
  Business,
  LocationOn,
  CalendarToday,
  Phone,
  Email,
  DragIndicator,
  Add,
} from '@mui/icons-material';
import { DataService } from '../services/dataService';
import { Referral, ReferralStatus } from '../types';

const LeadManagementScreen: React.FC = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');

  const { data: referrals, isLoading, error } = useQuery<Referral[]>({
    queryKey: ['referrals'],
    queryFn: DataService.getReferrals,
  });

  const { data: users } = useQuery({
    queryKey: ['users'],
    queryFn: DataService.getUsers,
  });

  const handleStatusFilterChange = (event: SelectChangeEvent) => {
    setStatusFilter(event.target.value);
  };

  const filteredReferrals = referrals?.filter(referral => {
    const matchesSearch = referral.sellerFirstName.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         referral.sellerLastName.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         referral.propertyAddress.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === 'all' || referral.status === statusFilter;
    return matchesSearch && matchesStatus;
  }) || [];

  const getStatusColor = (status: ReferralStatus) => {
    switch (status) {
      case ReferralStatus.PENDING: return 'default';
      case ReferralStatus.CONTACTED: return 'info';
      case ReferralStatus.MEETING: return 'warning';
      case ReferralStatus.SOLD: return 'success';
      case ReferralStatus.LOST: return 'error';
      case ReferralStatus.EXPIRED: return 'default';
      default: return 'default';
    }
  };

  const getStatusLabel = (status: ReferralStatus) => {
    switch (status) {
      case ReferralStatus.PENDING: return 'Nouveau';
      case ReferralStatus.CONTACTED: return 'Contacté';
      case ReferralStatus.MEETING: return 'Rendez-vous';
      case ReferralStatus.SOLD: return 'Vendu';
      case ReferralStatus.LOST: return 'Perdu';
      case ReferralStatus.EXPIRED: return 'Expiré';
      default: return status;
    }
  };

  const getReferralsByStatus = (status: ReferralStatus) => {
    return filteredReferrals.filter(referral => referral.status === status);
  };

  const getUserName = (userId: string) => {
    const user = users?.find(u => u.id === userId);
    return user ? `${user.firstName} ${user.lastName}` : 'Inconnu';
  };

  const getAgencyName = (agencyId?: string) => {
    if (!agencyId) return 'Non assignée';
    const agency = users?.find(u => u.id === agencyId);
    return agency ? `${agency.firstName} ${agency.lastName}` : 'Inconnue';
  };

  const pipelineStages = [
    ReferralStatus.PENDING,
    ReferralStatus.CONTACTED,
    ReferralStatus.MEETING,
    ReferralStatus.SOLD,
  ];

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
        <Typography>Chargement...</Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography color="error">Erreur lors du chargement des leads</Typography>
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4">Gestion des leads</Typography>
      </Box>

      {/* Search and Filters */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
          <TextField
            placeholder="Rechercher un lead..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search />
                </InputAdornment>
              ),
            }}
            sx={{ minWidth: 300 }}
          />
          <FormControl sx={{ minWidth: 150 }}>
            <InputLabel>Statut</InputLabel>
            <Select
              value={statusFilter}
              label="Statut"
              onChange={handleStatusFilterChange}
            >
              <MenuItem value="all">Tous les statuts</MenuItem>
              <MenuItem value="pending">Nouveau</MenuItem>
              <MenuItem value="contacted">Contacté</MenuItem>
              <MenuItem value="meeting">Rendez-vous</MenuItem>
              <MenuItem value="sold">Vendu</MenuItem>
              <MenuItem value="lost">Perdu</MenuItem>
              <MenuItem value="expired">Expiré</MenuItem>
            </Select>
          </FormControl>
        </Box>
      </Paper>

      {/* Pipeline View */}
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', lg: 'repeat(4, 1fr)' }, gap: 3 }}>
        {pipelineStages.map((status) => {
          const stageReferrals = getReferralsByStatus(status);
          return (
            <Paper key={status} sx={{ p: 2 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Box>
                  <Typography variant="h6" gutterBottom>
                    {getStatusLabel(status)}
                  </Typography>
                  <Badge badgeContent={stageReferrals.length} color="primary">
                    <Typography variant="body2" color="textSecondary">
                      {stageReferrals.length} lead{stageReferrals.length !== 1 ? 's' : ''}
                    </Typography>
                  </Badge>
                </Box>
                <Chip 
                  label={getStatusLabel(status)} 
                  color={getStatusColor(status) as any}
                  size="small"
                />
              </Box>

              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                {stageReferrals.map((referral) => (
                  <Card key={referral.id} sx={{ cursor: 'pointer', '&:hover': { boxShadow: 3 } }}>
                    <CardContent sx={{ p: 2 }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>
                          {referral.sellerFirstName} {referral.sellerLastName}
                        </Typography>
                        <IconButton size="small">
                          <MoreVert />
                        </IconButton>
                      </Box>

                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                        <LocationOn fontSize="small" color="action" />
                        <Typography variant="caption" color="textSecondary">
                          {referral.propertyAddress}
                        </Typography>
                      </Box>

                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                        <Person fontSize="small" color="action" />
                        <Typography variant="caption" color="textSecondary">
                          Parrain: {getUserName(referral.recommenderId)}
                        </Typography>
                      </Box>

                      {referral.agencyId && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                          <Business fontSize="small" color="action" />
                          <Typography variant="caption" color="textSecondary">
                            Agence: {getAgencyName(referral.agencyId)}
                          </Typography>
                        </Box>
                      )}

                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                        <CalendarToday fontSize="small" color="action" />
                        <Typography variant="caption" color="textSecondary">
                          {referral.createdAt.toLocaleDateString('fr-FR')}
                        </Typography>
                      </Box>

                      <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
                        <Tooltip title="Appeler">
                          <IconButton size="small" color="primary">
                            <Phone fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Envoyer email">
                          <IconButton size="small" color="primary">
                            <Email fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </CardContent>
                  </Card>
                ))}

                {stageReferrals.length === 0 && (
                  <Box sx={{ 
                    p: 3, 
                    textAlign: 'center', 
                    border: '2px dashed #e0e0e0', 
                    borderRadius: 1,
                    color: 'text.secondary'
                  }}>
                    <Typography variant="body2">
                      Aucun lead dans cette étape
                    </Typography>
                  </Box>
                )}
              </Box>
            </Paper>
          );
        })}
      </Box>

      {/* Additional Stats */}
      <Box sx={{ mt: 4 }}>
        <Paper sx={{ p: 3 }}>
          <Typography variant="h6" gutterBottom>
            Statistiques des leads
          </Typography>
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(4, 1fr)' }, gap: 3 }}>
            <Box sx={{ textAlign: 'center' }}>
              <Typography variant="h4" color="primary">
                {filteredReferrals.length}
              </Typography>
              <Typography variant="body2" color="textSecondary">
                Total des leads
              </Typography>
            </Box>
            <Box sx={{ textAlign: 'center' }}>
              <Typography variant="h4" color="info.main">
                {getReferralsByStatus(ReferralStatus.CONTACTED).length}
              </Typography>
              <Typography variant="body2" color="textSecondary">
                Contactés
              </Typography>
            </Box>
            <Box sx={{ textAlign: 'center' }}>
              <Typography variant="h4" color="warning.main">
                {getReferralsByStatus(ReferralStatus.MEETING).length}
              </Typography>
              <Typography variant="body2" color="textSecondary">
                Rendez-vous
              </Typography>
            </Box>
            <Box sx={{ textAlign: 'center' }}>
              <Typography variant="h4" color="success.main">
                {getReferralsByStatus(ReferralStatus.SOLD).length}
              </Typography>
              <Typography variant="body2" color="textSecondary">
                Vendus
              </Typography>
            </Box>
          </Box>
        </Paper>
      </Box>
    </Box>
  );
};

export default LeadManagementScreen;
